import React, { useEffect, useState } from "react";
import { Card, Button, Spinner, Form, Alert, Row } from "react-bootstrap";
import { ChevronDown, ChevronUp } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { techStacksList } from "../../utils/common";

const techStacks = techStacksList.filter(t => t !== "");

const LearnWithAIPage = () => {
  const student = getCurrentUser();
  const [showInstructions, setShowInstructions] = useState(() => window.innerWidth >= 768);
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [techStack, setTechStack] = useState(localStorage.getItem("aiTechStack") || "");
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(null);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);

  const difficultyOrder = ["easy", "medium", "hard"];
  const [difficultyIndex, setDifficultyIndex] = useState(0);

  const fetchQuestion = async () => {
    if (!student?.studentId || !techStack) return;

    setLoading(true);
    setFeedback(null);
    setScore(null);
    setError("");
    setAnswer("");

    try {
      const res = await apiClient.get("/ai/ai-questions/next", {
        params: {
          studentId: student.studentId,
          difficulty: difficultyOrder[difficultyIndex],
          techStack,
        },
      });
      setQuestion(res.data);
    } catch (err) {
      if (difficultyIndex < 2) {
        setDifficultyIndex(difficultyIndex + 1);
      } else {
        setError("No more questions available for the selected tech stack.");
        setQuestion(null);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (techStack) fetchQuestion();
  }, [difficultyIndex, techStack]);

  const handleSubmit = async () => {
    if (!answer.trim()) {
      setError("Please enter your answer before submitting.");
      return;
    }

    setEvaluating(true);
    setError("");
    setFeedback(null);
    setScore(null);

    try {
      const res = await apiClient.post("/ai/ai-evaluate", {
        studentId: student.studentId,
        questionId: question._id,
        answer: answer.trim(),
      });

      setScore(res.data.score);
      setFeedback(res.data.feedback);
      setRetrying(true);
    } catch (err) {
      setError("Evaluation failed. Please try again.");
    }

    setEvaluating(false);
  };

  const handleRetry = () => {
    setRetrying(false);
    setScore(null);
    setFeedback(null);
    setError("");
  };

  const handleNext = () => {
    setRetrying(false);
    fetchQuestion();
  };

  const handleTechStackChange = (e) => {
    const value = e.target.value;
    setTechStack(value);
    localStorage.setItem("aiTechStack", value);
    setDifficultyIndex(0);
    setQuestion(null);
    setFeedback(null);
    setScore(null);
    setAnswer("");
    setError("");
  };


  // Disable copy/paste/drag in the answer textarea
  const handlePaste = (e) => {
    e.preventDefault();
  };
  const handleCopy = (e) => {
    e.preventDefault();
  };
  const handleDragStart = (e) => {
    e.preventDefault();
  };
  const handleDrop = (e) => {
    e.preventDefault();
  };

  const handleSkip = () => {
    handleNext();
    setAnswer("");
  };

  return (
    <div>
      <Row className="g-3 mb-3">
        <div className="col-md-4">
          <Card className="mb-3">
            <Card.Header
              className="d-flex align-items-center justify-content-between"
              style={{
                cursor: "pointer",
                userSelect: "none",
                background: "transparent",
                borderBottom: showInstructions ? undefined : "none",
                borderRadius: showInstructions ? undefined : "calc(0.375rem - 1px)",
                padding: "0.75rem 1rem",
              }}
              onClick={() => setShowInstructions((v) => !v)}
            >
              <span className="fw-semibold mb-0" style={{ fontSize: 15 }}>Instructions</span>
              {showInstructions
                ? <ChevronUp size={16} className="text-muted" />
                : <ChevronDown size={16} className="text-muted" />}
            </Card.Header>
            {showInstructions && (
            <Card.Body>
              <div className="text-muted mb-3" style={{ fontSize: 14 }}>Read instructions carefully</div>
              <p className="text-muted">Select a tech stack to practice questions.</p>
              <p className="text-muted">Answer the question in detail, including explanations and examples to gain high scores.</p>
              <p className="text-muted">Once you submit your answer for a question, you can't re-attempt it. / Same question will not appear again.</p>
              <p className="text-muted">Click "Submit Answer" to get AI feedback and score.</p>
              <p className="text-muted">You can retry the same question or move to the next one.</p>
              <p className="text-muted">Use the tech stack dropdown to switch topics.</p>
              <p className="text-muted">Your progress is saved automatically.</p>
              <p className="text-muted">Have fun learning and improving your skills!</p>
              <div className="mt-3 text-muted">
                <p>Score is out of 100. Aim for 50 or above to pass!</p>
                <p>Feedback will help you understand your strengths and areas for improvement.</p>
                <p>Scoring Criteria:</p>
                <ul>
                  <li>0-49: Lack of understanding or incomplete answer</li>
                  <li>50-70: Good understanding but missing some key points</li>
                  <li>71-85: Strong grasp of concepts with minor errors or missing examples</li>
                  <li>86-100: Excellent mastery with examples and thorough explanations</li>
                </ul>
                </div>
            </Card.Body>
            )}
          </Card>
        </div>
        <div className="col-md-8">
          <div>
            {/* Tech Stack Selection */}
            <Card className="mb-3">
              <Card.Body>
                <Form.Group>
                  <Form.Label>Select Tech Stack to Practice</Form.Label>
                  <Form.Select value={techStack} onChange={handleTechStackChange}>
                    <option value="">-- Select a Tech Stack --</option>
                    {techStacks.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Main Content */}
            {techStack && (
              <>
                {loading ? (
                  <div className="text-center my-5"><Spinner animation="border" /></div>
                ) : error ? (
                  <Alert variant="warning" className="text-center">{error}</Alert>
                ) : question ? (
                  <Card>
                    <Card.Body>
                      <div className="mb-3">
                        <div className="fw-semibold mb-2">Question:</div>
                        <div style={{ fontSize: 16 }}>{question.question}</div>
                      </div>

                      <Form.Group className="mb-3">
                        <Form.Label>
                          Your Answer (include explanation & example if needed)
                          <span className="text-danger ms-2" style={{ fontSize: 13 }}>
                            (Minimum 50 characters)
                          </span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={8}
                          value={answer}
                          onChange={(e) => setAnswer(e.target.value)}
                          placeholder="Type your answer here..."
                          onPaste={handlePaste}
                          onCopy={handleCopy}
                          onDragStart={handleDragStart}
                          onDrop={handleDrop}
                          minLength={50}
                          required
                          isInvalid={answer.length > 0 && answer.length < 50}
                        />
                        <div className="text-end text-muted" style={{ fontSize: 13 }}>
                          {answer.length} / 50 characters
                        </div>
                      </Form.Group>

                      {!score && (
                        <>
                        <Button
                          variant="success"
                          disabled={evaluating || !answer.trim() || answer.length < 50}
                          onClick={handleSubmit}
                        >
                          {evaluating ? "Evaluating..." : "Submit Answer"}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={handleSkip}
                          className="ms-2"
                        >
                          Skip Question
                        </Button>
                        </>
                      )}

                      {score !== null && (
                        <div className="mt-4">
                          <Alert variant={score >= 50 ? "success" : "danger"}>
                            <div><strong>Score:</strong> {score}/100</div>
                            <div><strong>Feedback:</strong> {feedback}</div>
                          </Alert>

                          <div className="d-flex justify-content-end mt-3">
                            <Button variant="outline-dark" className="me-2" onClick={handleNext}>Next Question</Button>
                            <Button variant="outline-primary" onClick={handleRetry}>Retry This</Button>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ) : null}
              </>
            )}
          </div>
        </div>
      </Row>
    </div>
  );
};

export default LearnWithAIPage;
